import { defineNuxtPlugin, useRouter, useRuntimeConfig, useRequestEvent } from '#imports'
import * as Sentry from '@sentry/nuxt'
import cookie from 'cookie'

import { db, deleteAccountData } from '@/lib/db'

import md5 from 'md5'

import { v4 as uuid } from 'uuid'

import Themes from '@/components/layout/themes'
import { useAuthStore } from '~/stores/useAuthStore'
import { useUIStore } from '~/stores/useUIStore'
import { useCustomerStore } from '~/stores/useCustomerStore'
import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { useSegmentStore } from '~/stores/useSegmentStore'
import { useProductStore } from '~/stores/useProductStore'
import { useIntegrationStore } from '~/stores/useIntegrationStore'
import { useFormStore } from '~/stores/useFormStore'
import { useVoucherStore } from '~/stores/useVoucherStore'
import { useCustomFieldsStore } from '~/stores/useCustomFieldsStore'
import { useDiscountModelStore } from '~/stores/useDiscountModelStore'
import { useChargeModelStore } from '~/stores/useChargeModelStore'
import { usePaymentModelStore } from '~/stores/usePaymentModelStore'
import { useTaxStore } from '~/stores/useTaxStore'
import { usePassStore } from '~/stores/usePassStore'
import { isRecentLocationCaptureEnabled, resolveMarketingOrigin } from '@/utils/marketing'
import { markRecentLocationCapture } from '@/utils/recentLocationCapture'

declare const window: any

const INFO_SYNC_MAX_AGE_MS = 60 * 60 * 1000

class Account {
  guid: string
  info: any
  locked: boolean
  attempts: number
  lastActiveAt: number
  history: string
  panel: string
  home: string
  sort: Record<string, any>
  scheduleView: string
  created_at: Date
  updated_at: Date

  constructor() {
    this.guid = uuid()

    this.info = null
    this.locked = false
    this.attempts = 0
    this.lastActiveAt = Date.now()
    this.history = '/'
    this.panel = 'modal'
    this.home = '/'
    this.sort = {}
    this.scheduleView = '/schedule/day'

    this.created_at = new Date()
    this.updated_at = new Date()
  }

  setInfo(data: any) {
    this.info = data
    this.updated_at = new Date()
  }
}

export interface AuthPlugin {
  init(): void
  initFromCredentials(): Promise<void>
  setupFocusSync(): void
  login(callback: () => Promise<any>, options?: { sync?: boolean }): Promise<void>
  passwordLogin(params: { idLocation: string; email: string; password: string }): Promise<void>
  codeLogin(params: { idLocation: string; email: string; token: string; code: string }): Promise<void>
  tokenLogin(params: { token: string; sync?: boolean }): Promise<void>
  requestCode(params: { idLocation: string; email: string }): Promise<any>
  find(params: { email: string; token: string; code: string }): Promise<any>
  resetPassword(params: { idLocation: string; email: string; password: string; confirm: string; token: string; code: string }): Promise<any>
  checkPin(params: { pin: string }): Promise<any>
  logout(options?: { revokeTokens?: boolean }): Promise<void>
  lock(): void
  touch(): void
  unlock(options?: { pin?: string; override?: boolean; sync?: boolean }): Promise<void>
  switch(): void
  redirect(): void
  redirectThroughRecentLocationCapture(idLocation: string | number): void
  addAccount(account: Account): boolean
  fetchInfo(): Promise<any>
  fetchInfoOnce(): Promise<any>
  setActive(account: Account | null, options?: { sync?: boolean; info?: any }): Promise<void>
  syncInfo(): Promise<void>
  syncActive(options?: { lock?: boolean; info?: any }): Promise<void>
  syncWorkspace(options?: { force?: boolean; change?: boolean }): Promise<void>
  resetHydration(): void
  helpscout(): void
  clear(): Promise<void>
  setMaintenance(params: { title: string; content: string }): void
  setHome(home: string): void
  setSort(params: { model: string; value: any }): void
  setLock(locked: boolean): void
  setHistory(path: string): void
  setAttempts(value: number): void
  setReady(value: boolean): void
  setTheme(theme: string): void
  readonly clientId: string
  readonly scopes: string[]
  readonly headers: any
  readonly host: string
  readonly root: string
  readonly domain: string
  readonly location: string | null
  readonly url: string
  readonly active: any
  readonly accounts: any
  readonly accountIDs: string[]
  readonly info: any
  readonly locked: boolean
  readonly ready: boolean
  readonly home: string
  readonly sort: any
  readonly theme: any
}

// Centralised Sentry tag-setting so it fires regardless of which code path
// ends up with the info object. Called from fetchInfo() (classic path) and
// login() (post-refactor path that shares one fetch across setActive +
// syncActive). Idempotent — double-calling just overwrites with the same tags.
function applySentryTags(info: any) {
  if (!info) return
  Sentry.setTags({
    location_name: info?.location?.title,
    location_id: info?.location?.idLocation,
    employee_name: info?.employee?.displayName,
    employee_id: info?.employee?.idEmployee,
    admin_link: `admin.ovatu.dev/location/${info?.location?.idLocation}`,
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const req = import.meta.server ? useRequestEvent()?.node?.req : null
  const pinia = nuxtApp.$pinia
  const router = useRouter()
  const config = useRuntimeConfig()

  // Auth and UI stores are safe to init here — they are pure state (no useNuxtApp() calls)
  const authStore = useAuthStore(pinia)
  const uiStore = useUIStore(pinia)

  const { $api, $colorMode } = nuxtApp as any

  // Static cookies — set up at plugin level for SSR-safe access
  const clientIdCookie = useCookie('auth.client_id')
  const scopeCookie = useCookie('auth.scope')
  const appTokenCookie = useCookie('auth.app_token')
  const authCookieRef = useCookie('auth')
  const layoutCookie = useCookie('ovatu.layout')
  const themeCookie = useCookie('ovatu.theme')
  const recentLocations = useRecentLocations()
  let lastInfoSyncAt: number | null = null

  const plugin: AuthPlugin = {
    init() {
      clientIdCookie.value = this.clientId
      scopeCookie.value = this.scopes.join(' ')

      const token = appTokenCookie.value

      if (token) {
        this.tokenLogin({ token, sync: false })
      } else {
        // Check if we have credentials from an external app (e.g. Flutter webview)
      }

      if (layoutCookie.value === 'app') {
        if ($colorMode) {
          $colorMode.preference = 'system'
        }

        this.initFromCredentials()
      }

      if (import.meta.client) {
        this.clear()
        this.setupFocusSync()
      }
    },

    setupFocusSync() {
      if (import.meta.server) {
        return
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden || !this.active) {
          return
        }

        const now = Date.now()
        if (!lastInfoSyncAt || now - lastInfoSyncAt > INFO_SYNC_MAX_AGE_MS) {
          this.syncInfo()
        }
      })
    },

    async initFromCredentials() {
      const authData = authCookieRef.value

      if (!authData) { return }

      let auth: any
      try {
        auth = typeof authData === 'string' ? JSON.parse(decodeURIComponent(authData)) : authData
      } catch (e) {
        return
      }

      const activeGuid = auth?.auth?.active
      if (!activeGuid) { return }

      const existingAccount = authStore.accounts[activeGuid]
      if (existingAccount?.info) { return }

      // Dynamic cookie name — parse manually since useCookie() requires static names at setup
      let credentialsRaw: string | undefined
      if (import.meta.server && req?.headers?.cookie) {
        credentialsRaw = cookie.parse(req.headers.cookie)[`auth.credentials.${activeGuid}`]
      } else if (import.meta.client) {
        credentialsRaw = cookie.parse(document.cookie)[`auth.credentials.${activeGuid}`]
      }

      if (!credentialsRaw) { return }

      let credentials: any
      try {
        credentials = JSON.parse(decodeURIComponent(credentialsRaw))
      } catch (e) {
        return
      }

      if (!credentials?.access_token) { return }

      this.setReady(false)

      const account = new Account()
      account.guid = activeGuid

      authStore.addAccount(account)

      if (themeCookie.value) {
        this.setTheme(themeCookie.value)
      }

      try {
        const info = (await $api.auth.info()).data
        lastInfoSyncAt = Date.now()
        account.setInfo(info)

        authStore.setAccountInfo({ guid: activeGuid, info })

        this.setTheme(info?.location?.locationTheme?.code || 'ovatu')
      } catch (error) {
        authStore.removeAccount(account)
      } finally {
        this.setReady(true)
      }
    },

    async login(callback, { sync = true } = {}) {
      this.setReady(false)

      const account = new Account()
      let recentLocationId: string | number | null = null

      authStore.setActive(account.guid)

      try {
        const response = await callback()

        // One authoritative $api.auth.info() fetch, reused across setActive()
        // and syncActive() below. Previously each of those called fetchInfo()
        // internally, resulting in three $api.auth.info() round-trips per
        // login. See .docs/performance.md.
        const info = (await $api.auth.info()).data
        applySentryTags(info)
        account.setInfo(info)

        const accounts = authStore.accounts
        const id = account.info.employee.idEmployee

        const existing = Object.values(accounts).find((a: any) => a.info.employee.idEmployee === id)

        if (existing) {
          authStore.removeAccount(existing)
        }

        this.addAccount(account)

        await this.setActive(account, { info })
        this.touch()

        await this.syncActive({ info })

        recentLocationId = info.location.idLocation

        recentLocations.remember(recentLocationId)

        const captureRecentLocation = isRecentLocationCaptureEnabled(String(config.public.api))
        const willReloadForRecentLocation = sync && !!recentLocationId && captureRecentLocation

        if (sync) {
          if (recentLocationId && captureRecentLocation) {
            this.redirectThroughRecentLocationCapture(recentLocationId)
          } else {
            this.redirect()
          }
        }

        // The recent-location capture performs a full document navigation, so
        // the mounted app on the return trip will start its workspace refresh.
        // All other login paths stay in this document and need to start it here.
        if (!willReloadForRecentLocation) {
          void this.syncWorkspace({ change: true })
        }
      } catch (error) {
        this.setActive(null)
        this.setReady(true)
        console.log(error)
        throw error
      }
    },

    async passwordLogin({ idLocation, email, password }) {
      return await this.login(() => $api.auth.login({ oauth_client: this.clientId, location: idLocation, email, password }))
    },

    async codeLogin({ idLocation, email, token, code }) {
      return await this.login(() => $api.auth.codeLogin({ oauth_client: this.clientId, location: idLocation, email, token, code }))
    },

    async tokenLogin({ token, sync = true }) {
      return await this.login(() => $api.auth.tokenLogin({ oauth_client: this.clientId, token }), { sync })
    },

    async requestCode({ idLocation, email }) {
      return await $api.auth.requestCode({ oauth_client: this.clientId, location: idLocation, email })
    },

    async find({ email, token, code }) {
      return await $api.auth.accounts({ oauth_client: this.clientId, email, token, code })
    },

    async resetPassword({ idLocation, email, password, confirm, token, code }) {
      return await $api.auth.resetPassword({ oauth_client: this.clientId, location: idLocation, email, password, confirm, token, code })
    },

    async checkPin({ pin }) {
      return await $api.auth.checkPin(pin)
    },

    async logout({ revokeTokens = true } = {}) {
      const account = this.active

      if (revokeTokens) {
        try {
          const success = await $api.auth.logout()
        } catch (e) {
          Sentry.captureException(e)
        }
      }

      this.setActive(null)

      if (account) {
        authStore.removeAccount(account)
        router.push('/auth/login/email')
      }
    },

    lock() {
      authStore.setLock({ locked: true })
    },

    touch() {
      authStore.setLastActive({})
    },

    async unlock({ pin, override = false, sync = false } = {}) {
      const employee = this.active.info.employee
      const pinHash = employee.pinHash
      const idEmployee = employee.idEmployee

      let correct = override || pinHash === md5(pin + idEmployee + 'e475a4a8df94')

      if (!correct) {
        try {
          correct = await this.checkPin({ pin: pin! })
        } catch (error) {
          correct = false
        }
      }

      if (correct) {
        authStore.setLock({ locked: false })
        this.touch()

        if (sync) {
          void this.syncActive().then(() => this.syncWorkspace({ change: true }))
        }

        this.setAttempts(0)

        this.redirect()
      } else {
        throw new Error('Invalid pin')
      }
    },

    switch() {
      router.push('/auth/switch')
    },

    redirect() {
      const redirect = authStore.auth.redirect

      if (redirect) {
        router.push(redirect)
        authStore.setAuthField({ key: 'redirect', value: null })
      } else {
        router.push(this.active?.history || '/')
      }
    },

    redirectThroughRecentLocationCapture(idLocation) {
      const redirect = authStore.auth.redirect
      const destination = redirect || this.active?.history || '/'

      if (redirect) {
        authStore.setAuthField({ key: 'redirect', value: null })
      }

      const returnUrl = new URL(router.resolve(destination).href, window.location.origin)
      const marketingOrigin = resolveMarketingOrigin(String(config.public.api))
      const captureUrl = new URL('/login/recent/capture', marketingOrigin)

      captureUrl.searchParams.set('location', String(idLocation))
      captureUrl.searchParams.set('return', returnUrl.toString())

      const activeGuid = this.active?.guid
      if (activeGuid) {
        try {
          markRecentLocationCapture(window.sessionStorage, activeGuid)
        } catch {
          // Accessing sessionStorage itself can fail in restricted contexts.
          // The capture remains valid and the return trip will use normal sync.
        }
      }

      window.location.assign(captureUrl.toString())
    },

    addAccount(account) {
      const accounts = authStore.accounts
      const accountIDs = Object.values(accounts).map((a: any) => a.info.employee.idEmployee)

      const ID = account.info.employee.idEmployee

      if (accountIDs.includes(ID)) {
        // Account already logged in
      }

      authStore.addAccount(account)

      return true
    },

    async fetchInfo() {
      try {
        const info = (await $api.auth.info()).data
        lastInfoSyncAt = Date.now()

        applySentryTags(info)

        return info
      } catch (error) {
        console.log(error)
      }
    },

    async fetchInfoOnce() {
      if (!this.info) {
        return await this.fetchInfo()
      }

      return this.info
    },

    async setActive(account, { sync = false, info: providedInfo } = {}) {
      authStore.setActive(account?.guid || null)

      if (account) {
        this.resetHydration()

        // Caller (e.g. login()) can pass a freshly-fetched info object to skip
        // a redundant $api.auth.info() round-trip here. See .docs/performance.md.
        const info = providedInfo ?? await this.fetchInfo()

        if (info) {
          authStore.setAccountInfo({ info })

          if (sync) {
            await this.syncActive({ info })
            void this.syncWorkspace({ change: true })
          }
        }
      }
    },

    async syncInfo() {
      const active = this.active

      if (active) {
        const info = await this.fetchInfo()
        const inactive = info?.plan?.isPlanSubscriptionStateInactive

        if (inactive && !info?.adminUser) {
          router.push('/account/inactive')

          this.helpscout()

          this.setTheme(info?.location?.locationTheme?.code || 'ovatu')

          this.setReady(true)
        } else if (info) {
          authStore.setAccountInfo({ info })
        }
      }
    },

    async syncActive({ lock = false, info: providedInfo } = {}) {
      const active = this.active

      if (lock && active?.info?.location?.webPINRequired && !!active?.info?.employee?.pinHash) {
        authStore.setLock({ locked: true })
        this.setReady(true)

        return
      }

      if (!active) {
        this.setReady(true)
        return
      }

      this.setReady(false)

      try {
        // Caller (e.g. login()) can pass a freshly-fetched info object to skip
        // a redundant $api.auth.info() round-trip here. See .docs/performance.md.
        const info = providedInfo ?? await this.fetchInfo()

        const inactive = info?.plan?.isPlanSubscriptionStateInactive

        if (inactive && !info?.adminUser) {
          router.push('/account/inactive')

          this.helpscout()
        } else if (info) {
          authStore.setAccountInfo({ info })

          ;nuxtApp.$centrifuge.connect()

          if (layoutCookie.value !== 'app') {
            this.helpscout()
          }

          this.setTheme(info?.location?.locationTheme?.code || 'ovatu')
        }
      } finally {
        this.setReady(true)
      }
    },

    async syncWorkspace({ force = false, change = false } = {}) {
      const active = this.active
      const inactive = active?.info?.plan?.isPlanSubscriptionStateInactive

      if (!active || (inactive && !active?.info?.adminUser) || layoutCookie.value === 'app') {
        return
      }

      // Workspace reference data is useful throughout the app, but it is not
      // part of authentication. Keep these independent syncs parallel and let
      // callers run this method in the background after the session is ready.
      // Individual stores swallow normal API failures; the outer catch protects
      // against unexpected hydration/IndexedDB failures becoming unhandled.
      try {
        const options = { force, change }
        await Promise.all([
          useCustomerStore(pinia).sync(options),
          useServiceStore(pinia).sync(options),
          useEmployeeStore(pinia).sync(options),
          useSegmentStore(pinia).sync(options),
          useProductStore(pinia).sync(options),
          useIntegrationStore(pinia).sync(options),
          useFormStore(pinia).sync(options),
          useVoucherStore(pinia).sync(options),
          useCustomFieldsStore(pinia).sync(options),
          useDiscountModelStore(pinia).sync(options),
          useChargeModelStore(pinia).sync(options),
          usePaymentModelStore(pinia).sync(options),
          useTaxStore(pinia).sync(options),
          usePassStore(pinia).sync(options),
        ])
      } catch (error) {
        console.error('[auth] Failed to refresh workspace cache', error instanceof Error ? error.message : String(error))
      }
    },

    resetHydration() {
      // Data sync stores accessed lazily
      useCustomerStore(pinia).hydrated = false
      useServiceStore(pinia).hydrated = false
      useEmployeeStore(pinia).hydrated = false
      useSegmentStore(pinia).hydrated = false
      useProductStore(pinia).hydrated = false
      useIntegrationStore(pinia).hydrated = false
      useFormStore(pinia).hydrated = false
      useVoucherStore(pinia).hydrated = false
      useCustomFieldsStore(pinia).hydrated = false
      useDiscountModelStore(pinia).hydrated = false
      useChargeModelStore(pinia).hydrated = false
      usePaymentModelStore(pinia).hydrated = false
      useTaxStore(pinia).hydrated = false
      usePassStore(pinia).hydrated = false
    },

    helpscout() {
      if (config.public.instance !== 'demo') {
        const employee = this.active?.info?.employee
        const location = this.active?.info?.location

        if (typeof window !== 'undefined' && employee && location) {
          const data = {
            idClient: location.idClient,
            idLocation: location.idLocation,
            slug: location.slug,
            name: employee.displayName,
            email: employee.email,
            role: employee.role?.name,
            signature: employee.helpscoutSignature,
          }

          try {
            window.Beacon?.('identify', data)
          } catch (e) {
            console.log(e)
          }
        }
      }
    },

    async clear() {
      if (import.meta.server) {
        return
      }

      // Find every accountId present in Dexie and drop data for any that
      // aren't in the active accountIDs set (i.e. accounts the user has
      // removed from the switcher). Uses the `accountId` secondary index
      // on `syncStates` as the authoritative list of seen accounts — if an
      // account ever synced, it has a row there.
      try {
        const activeGuids = new Set(this.accountIDs)
        const seenAccountIds = new Set<string>()

        await db.syncStates.each((row) => {
          seenAccountIds.add(row.accountId)
        })

        const orphaned = [...seenAccountIds].filter(id => !activeGuids.has(id))

        for (const accountId of orphaned) {
          await deleteAccountData(accountId)
        }
      } catch (error) {
        console.error('[auth] Failed to clear local cache keys', error instanceof Error ? error.message : String(error))
      }
    },

    setMaintenance({ title, content }) {
      uiStore.setMaintenance({ title, content })
    },

    setHome(home) {
      authStore.setHome({ home })
    },

    setSort({ model, value }) {
      authStore.setSort({ model, value })
    },

    setLock(locked) {
      authStore.setLock({ locked })
    },

    setHistory(path) {
      authStore.setHistory({ path })
    },

    setAttempts(value) {
      authStore.setAttempts({ value })
    },

    setReady(value) {
      authStore.setReady(value)
    },

    setTheme(theme) {
      uiStore.setTheme(theme)
    },

    get clientId() {
      return 'bcbc0d85416f43a8ba6c004a3f509692'
    },

    get scopes() {
      return ['employee']
    },

    get headers() {
      return req ? req.headers : window.location
    },

    get host() {
      return req ? req.headers.host : window.location.host
    },

    get root() {
      const parts = this.host.split('.')
      return parts.length > 2 ? parts[2] : parts[1]
    },

    get domain() {
      const parts = this.host.split('.')
      return parts.length > 2 ? parts[1] : parts[0]
    },

    get location() {
      if (config.public.forceLocation) {
        return config.public.forceLocation
      }

      const parts = this.host.split('.')
      return parts.length > 2 ? parts[0] : null
    },

    get url() {
      return this.domain + '.' + this.root
    },

    get active() {
      return authStore.activeAccount
    },

    get accounts() {
      return authStore.accountsList
    },

    get accountIDs() {
      return authStore.guids
    },

    get info() {
      return authStore.info
    },

    get locked() {
      return authStore.locked
    },

    get ready() {
      return authStore.ready
    },

    get home() {
      return authStore.home
    },

    get sort() {
      return authStore.sort
    },

    get theme() {
      const info = authStore.info
      const theme = uiStore.theme

      const code = theme || info?.location?.locationTheme?.code || 'ovatu'

      return (Themes as any)[code] || (Themes as any).ovatu
    },
  }

  nuxtApp.provide('auth', plugin)

  plugin.init()
})
